// Floating department cards anchored to the pods (floor view) and floor cards (building view),
// positioned every frame from scene.getAnchors() with transforms only. The scene lays the cards
// out (anchor.card: full card or compact pill, side, rectangle, leader line — web/scene/framing.js)
// so they never cover another pod's desks; a pill expands into the full card on hover or keyboard
// focus. When the scene gives no anchors (placeholder scene, no WebGL), the cards fall back to a
// responsive grid that also lists each department's agents, so the office stays usable without 3D.
import { h, icon, setText, setAttr, classNames, statusDot } from '../dom.js';
import { t } from '../i18n/fr.js';
import { deptColor, pad2 } from '../format.js';
import { floorDepartments, deptAgents, sortedFloors, normalizeStatus, can, activeRoutineCounts } from '../store.js';
import { floorNumber } from '../scene/layout.js';
import { studioDept } from './studio/model.js';

/** Time without any card anchor before switching to the grid fallback (ms). */
const FALLBACK_AFTER_MS = 1500;

/**
 * Screen position for a card from its anchor, rounded to half pixels (pure).
 * @returns {{x: number, y: number, visible: boolean}}
 */
export function cardPosition(anchor) {
  return {
    x: Math.round((Number(anchor?.x) || 0) * 2) / 2,
    y: Math.round((Number(anchor?.y) || 0) * 2) / 2,
    visible: anchor?.visible !== false && Number.isFinite(Number(anchor?.x)) && Number.isFinite(Number(anchor?.y)),
  };
}

/** Gap (px) between a card and its pod when the scene gives no layout (other scene builds). */
const DEFAULT_GAP = 12;

/**
 * Where a card element goes from its anchor (pure): the point its CSS `translate` hangs from
 * (bottom-centre above the pod, top-centre below, the inner edge's middle beside it), its mode
 * ('card' | 'pill') and side, and the leader line from the card edge to the pod, relative to that
 * point (length in px, angle in degrees, start offset along the card edge).
 * @param {{x:number, y:number, visible?:boolean, card?:{mode:string, side:string, rect:object, lead:object}|null}} anchor
 * @returns {{x:number, y:number, visible:boolean, mode:'card'|'pill', side:'top'|'bottom'|'left'|'right',
 *   pop:'up'|'down', lead:{dx:number, dy:number, len:number, ang:number}}}
 */
export function cardFrame(anchor) {
  const round = (v) => Math.round(v * 2) / 2;
  const c = anchor?.card;
  const r = c?.rect;
  const okRect = r && [r.left, r.top, r.right, r.bottom].every(Number.isFinite);
  const side = okRect && ['top', 'bottom', 'left', 'right'].includes(c.side) ? c.side : 'top';
  const mode = okRect && c.mode === 'pill' ? 'pill' : 'card';
  let x;
  let y;
  if (okRect) {
    const mx = (r.left + r.right) / 2;
    const my = (r.top + r.bottom) / 2;
    if (side === 'top') { x = mx; y = r.bottom; } else if (side === 'bottom') { x = mx; y = r.top; } else if (side === 'left') { x = r.right; y = my; } else { x = r.left; y = my; }
  } else {
    x = Number(anchor?.x);
    y = Number(anchor?.y) - DEFAULT_GAP;
  }
  const visible = anchor?.visible !== false && Number.isFinite(x) && Number.isFinite(y);
  let lead = { dx: 0, dy: 0, len: okRect ? 0 : DEFAULT_GAP, ang: 90 };
  const l = okRect ? c.lead : null;
  if (l && [l.x1, l.y1, l.x2, l.y2].every(Number.isFinite)) {
    const len = Math.hypot(l.x2 - l.x1, l.y2 - l.y1);
    lead = {
      dx: round(l.x1 - x), dy: round(l.y1 - y), len: round(len),
      ang: len ? Math.round((Math.atan2(l.y2 - l.y1, l.x2 - l.x1) * 180) / Math.PI * 10) / 10 : 90,
    };
  }
  // A pill opens its details above itself, or below when it sits near the top of the scene.
  const pop = okRect && r.top < 170 ? 'down' : 'up';
  return { x: visible ? round(x) : 0, y: visible ? round(y) : 0, visible, mode, side, pop, lead };
}

/**
 * Creates the cards layer controller.
 * @param {object} ctx app context
 * @param {{layer: HTMLElement, onDeptClick: (deptId: string) => void, onDeptFocus?: (deptId: string) => void,
 *   onFloorClick: (floorId: string) => void, onAgentClick: (agentId: string) => void,
 *   onDeptMenu?: (deptId: string, anchor: HTMLElement) => void, onDeptStudio?: (deptId: string) => void,
 *   onHover?: (target: {kind: string, id: string}|null) => void,
 *   getSafeRight?: () => number, onMetrics?: (size: {w?: number, h?: number, labelW?: number}) => void}} opts
 *   getSafeRight: x (layer px) beyond which floating panels cover the scene; onMetrics: the measured
 *   size of a full department card, or the width of the widest floor label (for scene.setCardMetrics)
 * @returns {{render(): void, update(anchors: object[]): void, isFallback(): boolean, destroy(): void}}
 */
export function createDeptCards(ctx, opts) {
  const { store } = ctx;
  const { layer } = opts;
  const cards = new Map();
  let modeKey = '';
  let fallback = false;
  let emptySince = 0;
  let measured = false;
  let labelW = 0;
  // Web fonts change the card height and the floor labels' width: measure again once they are in.
  globalThis.document?.fonts?.ready?.then?.(() => {
    measured = false;
    if (store.get().ui.view === 'building') measureFloors();
  }).catch?.(() => {});

  function hover(kind, id) {
    return {
      onMouseenter: () => opts.onHover?.({ kind, id }),
      onMouseleave: () => opts.onHover?.(null),
      onFocusin: () => opts.onHover?.({ kind, id }),
      onFocusout: () => opts.onHover?.(null),
    };
  }

  function stat(label, valueEl, cls) {
    return h('span', { class: classNames(['dc-stat', cls]) }, h('span', { class: 'mono-label' }, label), valueEl);
  }

  function buildDept(d) {
    const p = {
      name: h('span', { class: 'dc-name' }, d.name),
      pillNum: h('span', { class: 'dc-pill-num', 'aria-hidden': 'true' }),
      num: h('span', { class: 'dc-num' }),
      unit: h('span', { class: 'dc-unit mono-label' }),
      waiting: h('span', { class: 'dc-val' }),
      done: h('span', { class: 'dc-val' }),
      doing: h('b'), next: h('b'), failed: h('b'),
      // Active routines of the service, next to « À VENIR » (a daily routine is work to come too).
      routines: h('span', { class: 'dc-f dc-routines', hidden: true }),
      agents: h('div', { class: 'dc-agents' }),
      lead: h('span', { class: 'dc-lead', 'aria-hidden': 'true' }),
    };
    p.hit = h('button', {
      type: 'button', class: 'dc-hit',
      onClick: () => opts.onDeptClick?.(d.id),
      onDblclick: () => opts.onDeptFocus?.(d.id),
    }, h('span', { class: 'dc-dot', 'aria-hidden': 'true' }), p.name, p.pillNum);
    // Managers add an agent (admins also rename the service) from the card itself.
    p.menu = h('button', {
      type: 'button', class: 'dc-menu', hidden: true, 'aria-haspopup': 'menu', 'aria-label': t('dept.menuAria', { name: d.name }),
      title: t('dept.menuTitle'),
      onClick: (e) => { e.stopPropagation(); opts.onDeptMenu?.(d.id, p.menu); },
    }, icon('plus', { size: 13 }));
    // The service's Studio (docs/wip/SPEC-STUDIO.md §3), when its floor's studio is on.
    p.studio = h('button', {
      type: 'button', class: 'dc-studio', hidden: true, 'aria-label': t('dept.studioAria', { name: d.name }), title: t('dept.studioTitle'),
      onClick: (e) => { e.stopPropagation(); opts.onDeptStudio?.(d.id); },
    }, icon('film', { size: 13 }));
    const el = h('div', {
      class: 'scene-card dept-card', dataset: { dept: d.id, mode: 'card', side: 'top' },
      style: { '--dept': deptColor(d.color, d.id) }, ...hover('dept', d.id),
    },
    h('span', { class: 'dc-head' }, p.hit, p.studio, p.menu),
    h('span', { class: 'dc-body', 'aria-hidden': 'true' },
      h('span', { class: 'dc-count' }, p.num, p.unit),
      h('span', { class: 'dc-stats' }, stat(t('dept.waiting'), p.waiting, 'is-waiting'), stat(t('dept.doneToday'), p.done)),
      h('span', { class: 'dc-foot mono-label' },
        h('span', { class: 'dc-f' }, t('dept.doing'), p.doing),
        h('span', { class: 'dc-f' }, t('dept.next'), p.next),
        p.routines,
        h('span', { class: 'dc-f is-failed' }, t('dept.failed'), p.failed))),
    p.lead,
    p.agents);
    return { key: `dept:${d.id}`, kind: 'dept', id: d.id, el, p, x: NaN, y: NaN, visible: null, frame: '' };
  }

  function buildFloor(f, idx) {
    const p = {
      name: h('span', { class: 'fc-name' }, f.name),
      meta: h('span', { class: 'fc-meta mono-label' }),
      stats: h('span', { class: 'fc-stats mono-label' }),
    };
    const el = h('button', {
      type: 'button', class: 'scene-card floor-card', dataset: { floor: f.id },
      style: { '--dept': deptColor(f.color, f.id) },
      onClick: () => opts.onFloorClick?.(f.id), ...hover('floor', f.id),
    }, h('span', { class: 'fc-num' }, pad2(floorNumber(f, idx))), h('span', { class: 'fc-text' }, p.name, p.meta, p.stats));
    return { key: `floor:${f.id}`, kind: 'floor', id: f.id, el, p, x: NaN, y: NaN, visible: null };
  }

  function fillDept(card, d, s, routineCounts = activeRoutineCounts(s.routines)) {
    const agents = deptAgents(s.office.agents, d.id);
    const st = s.stats?.departments?.[d.id] || {};
    const n = agents.length;
    const { p } = card;
    setText(p.name, d.name);
    setText(p.pillNum, n);
    setText(p.num, n);
    setText(p.unit, t('dept.agents', { n }));
    setText(p.waiting, st.waiting || 0);
    setText(p.done, st.doneToday || 0);
    setText(p.doing, st.doing || 0);
    setText(p.next, st.next || 0);
    setText(p.failed, st.failedToday || 0);
    const routines = routineCounts[d.id] || 0;
    p.routines.hidden = !routines;
    setText(p.routines, routines ? t('dept.routines', { n: routines }) : '');
    setAttr(p.routines, 'title', routines ? t('dept.routinesTitle', { n: routines }) : null);
    card.el.classList.toggle('has-waiting', (st.waiting || 0) > 0);
    card.el.classList.toggle('has-failed', (st.failedToday || 0) > 0);
    card.el.classList.toggle('is-busy', (st.doing || 0) > 0);
    const selected = s.ui.deptFilter === d.id;
    card.el.classList.toggle('is-selected', selected);
    setAttr(p.hit, 'aria-pressed', String(selected));
    setAttr(p.hit, 'aria-label', t('dept.cardAria', { name: d.name, n }));
    p.studio.hidden = !(opts.onDeptStudio && studioDept(s.office, d.id));
    setAttr(p.studio, 'aria-label', t('dept.studioAria', { name: d.name }));
    if (p.menu) {
      p.menu.hidden = !(opts.onDeptMenu && (s.me?.role === 'admin' || can(s, 'manage', { floorId: d.floorId, deptId: d.id })));
      setAttr(p.menu, 'aria-label', t('dept.menuAria', { name: d.name }));
    }
    if (fallback) fillAgentChips(p, agents, s);
  }

  /**
   * Agent chips of the grid fallback, keyed by agent id: live status changes (every SSE `agent`
   * event) update them in place, so a chip keeps the keyboard focus.
   */
  function fillAgentChips(p, agents, s) {
    const chips = p.agentChips || (p.agentChips = new Map());
    const ids = new Set(agents.map((a) => a.id));
    for (const [id, c] of chips) if (!ids.has(id)) { c.el.remove(); chips.delete(id); }
    let prev = null;
    for (const a of agents) {
      const status = normalizeStatus(s.agentStatus.get(a.id) || a.status).status;
      let c = chips.get(a.id);
      if (!c || c.lead !== !!a.lead) {
        c?.el.remove();
        const dot = statusDot(status);
        const name = h('span', { class: 'ac-name' }, a.name);
        const el = h('button', {
          type: 'button', class: classNames(['agent-chip', { 'is-lead': a.lead }]), dataset: { agent: a.id },
          onClick: () => opts.onAgentClick?.(a.id),
        }, dot, a.lead ? h('span', { class: 'nt-star', 'aria-hidden': 'true' }, '★') : null, name);
        c = { el, dot, name, lead: !!a.lead };
        chips.set(a.id, c);
      }
      c.dot.setAttribute('class', classNames(['status-dot', `is-${status || 'idle'}`]));
      setText(c.name, a.name);
      setAttr(c.el, 'title', `${a.name} — ${a.role || ''}${a.engine ? ` · ${t(`engine.name.${a.engine}`)}` : ''}`);
      const expected = prev ? prev.nextSibling : p.agents.firstChild;
      if (c.el !== expected) p.agents.insertBefore(c.el, expected);
      prev = c.el;
    }
  }

  function fillFloor(card, f, s) {
    const depts = floorDepartments(s.office.departments, f.id);
    const agents = s.office.agents.filter((a) => a.floorId === f.id).length;
    const st = s.stats?.floors?.[f.id] || {};
    setText(card.p.name, f.name);
    setText(card.p.meta, `${depts.length} ${t('floor.depts', { n: depts.length })} · ${t('floor.agents', { n: agents })}`);
    const parts = [];
    if (st.doing) parts.push(`${t('dept.doing')} ${st.doing}`);
    if (st.waiting) parts.push(`${t('dept.waiting')} ${st.waiting}`);
    setText(card.p.stats, parts.join(' · '));
    card.el.classList.toggle('has-waiting', (st.waiting || 0) > 0);
    setAttr(card.el, 'aria-label', `${t('floor.cardAria', { name: f.name })} — ${card.p.meta.textContent}`);
  }

  function applyHover(s) {
    const hv = s.ui.hoverId;
    for (const card of cards.values()) card.el.classList.toggle('is-hover', !!hv && hv.kind === card.kind && hv.id === card.id);
  }

  /** Rebuilds the set of cards when the floor/view/office changes, then refreshes their content. */
  function render() {
    const s = store.get();
    const building = s.ui.view === 'building';
    const nextKey = building ? `building|${s.office.floors.length}` : `floor:${s.ui.floorId}`;
    const routineCounts = activeRoutineCounts(s.routines);
    const items = building
      ? sortedFloors(s.office.floors).map((f, i) => ({ key: `floor:${f.id}`, build: () => buildFloor(f, i), fill: (c) => fillFloor(c, f, s) }))
      : floorDepartments(s.office.departments, s.ui.floorId).map((d) => ({ key: `dept:${d.id}`, build: () => buildDept(d), fill: (c) => fillDept(c, d, s, routineCounts) }));
    if (nextKey !== modeKey) {
      modeKey = nextKey;
      emptySince = 0;
    }
    const keep = new Set();
    for (const it of items) {
      let card = cards.get(it.key);
      if (!card) {
        card = it.build();
        cards.set(it.key, card);
        if (fallback) card.el.classList.add('is-static');
        else card.el.classList.add('is-pending');
      }
      it.fill(card);
      keep.add(it.key);
      layer.appendChild(card.el); // keeps DOM order = display order
    }
    for (const [key, card] of cards) {
      if (!keep.has(key)) { card.el.remove(); cards.delete(key); }
    }
    layer.dataset.mode = building ? 'building' : 'floor';
    applyHover(s);
    if (building) measureFloors();
  }

  /**
   * Reports the widest floor label (building view) as soon as the labels exist, before the scene
   * frames the building, so it keeps room for them right of the slabs.
   */
  function measureFloors() {
    if (fallback || !opts.onMetrics) return;
    let w = 0;
    for (const card of cards.values()) if (card.kind === 'floor') w = Math.max(w, card.el.offsetWidth || 0);
    if (!w || Math.abs(w - labelW) < 4) return;
    labelW = w;
    opts.onMetrics({ labelW: w });
  }

  function setFallback(on) {
    if (fallback === on) return;
    fallback = on;
    layer.classList.toggle('is-fallback', on);
    for (const card of cards.values()) {
      card.el.classList.toggle('is-static', on);
      card.el.classList.remove('is-pending');
      if (on) {
        card.el.style.removeProperty('transform');
        card.x = NaN; card.y = NaN; card.visible = null; card.frame = '';
        card.el.classList.remove('is-offscreen');
        if (card.kind === 'dept') card.el.dataset.mode = 'card';
      }
    }
    store.setUi?.({ sceneFallback: on });
    render();
  }

  /** Applies the scene's layout of a department card: mode, side, leader line (only on change). */
  function placeDept(card, f) {
    const key = `${f.mode}|${f.side}|${f.pop}|${f.lead.dx}|${f.lead.dy}|${f.lead.len}|${f.lead.ang}`;
    if (key === card.frame) return;
    card.frame = key;
    const ds = card.el.dataset;
    if (ds.mode !== f.mode) ds.mode = f.mode;
    if (ds.side !== f.side) ds.side = f.side;
    if (ds.pop !== f.pop) ds.pop = f.pop;
    const st = card.p.lead.style;
    st.setProperty('--lx', `${f.lead.dx}px`);
    st.setProperty('--ly', `${f.lead.dy}px`);
    st.setProperty('--len', `${f.lead.len}px`);
    st.setProperty('--ang', `${f.lead.ang}deg`);
  }

  /** Reports the real size of a full card once it is on screen (the scene frames the floor with it). */
  function measure() {
    if (measured || fallback || !opts.onMetrics) return;
    for (const card of cards.values()) {
      if (card.kind !== 'dept' || !card.visible || card.el.dataset.mode !== 'card') continue;
      const w = card.el.offsetWidth;
      const hh = card.el.offsetHeight;
      if (!w || !hh) return;
      measured = true;
      opts.onMetrics({ w, h: hh });
      return;
    }
  }

  /** Positions the cards from the anchors of this frame (switches to the grid when none come). */
  function update(anchors) {
    if (!cards.size) return;
    let found = 0;
    for (const a of anchors) {
      if (a.kind !== 'dept' && a.kind !== 'floor') continue;
      const card = cards.get(`${a.kind}:${a.id}`);
      if (!card) continue;
      found += 1;
      if (fallback) continue;
      let pos;
      if (a.kind === 'floor') {
        // Floor labels sit right of the slab corner; flip them left when a panel would cover them.
        const width = card.width || (card.width = card.el.offsetWidth || 240);
        const side = a.x + 18 + width > (opts.getSafeRight?.() ?? Infinity) ? 'left' : 'right';
        if (card.el.dataset.side !== side) card.el.dataset.side = side;
        pos = cardPosition(a);
      } else {
        pos = cardFrame(a);
        if (pos.visible) placeDept(card, pos);
      }
      if (pos.visible !== card.visible) {
        card.visible = pos.visible;
        card.el.classList.toggle('is-offscreen', !pos.visible);
        card.el.classList.remove('is-pending');
        card.el.inert = !pos.visible;
      }
      if (!pos.visible || (pos.x === card.x && pos.y === card.y)) continue;
      card.x = pos.x;
      card.y = pos.y;
      card.el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0)`;
    }
    if (found) {
      emptySince = 0;
      if (fallback) setFallback(false);
      else measure();
    } else if (!fallback) {
      const now = Date.now();
      if (!emptySince) emptySince = now;
      else if (now - emptySince > FALLBACK_AFTER_MS) setFallback(true);
    }
  }

  const offs = [
    store.on('office', render),
    store.on('stats', render),
    store.on('routines', render),
    store.on('me', render),
    store.on('agentStatus', () => { if (fallback) render(); }),
    store.on('ui', (ui, state, prev) => {
      const p = prev?.ui || {};
      if (ui.view !== p.view || ui.floorId !== p.floorId || ui.deptFilter !== p.deptFilter) render();
      else if (ui.hoverId !== p.hoverId) applyHover(state);
    }),
  ];
  render();
  return {
    render,
    update,
    isFallback: () => fallback,
    destroy() { for (const off of offs) off(); for (const c of cards.values()) c.el.remove(); cards.clear(); },
  };
}
